import type {
  ProjectRead,
  SwitchingCreate,
  SwitchingDetailed,
  SwitchingRead,
  SwitchingUpdate,
} from '@gridcon-energy/schaltungstool-backend'
import { EquipmentRead, SubProjectRead, SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import useFetchData from '~/composables/common/useFetchData'
import { inject, provide, ref, watch } from 'vue'
import { injectBackendClient } from '~/api/backendClients'
import { injectHandleFetchError } from '~/plugins/vue-plugins/fetch-error-handler'
import { SwitchingEventsEnum } from '~/lib/activity'
import { DateRangeFilter, filterItemsByDateRange } from '~/composables/useActivityDataFilter'
import { SwitchingsRepositorySymbol } from '~/lib/symbols'
import { defineStore, storeToRefs } from 'pinia'
import { useSwitchingColumns } from '~/composables/tables/useSwitchingColumns'
import { SwitchingPremisesRepository } from '~/composables/repositories/switchingPremisesRepository'
import { SBlockRestrictionsRepository } from '~/composables/repositories/switchingBlockRestrictionsRepository'
import type { ListItemExtended } from '~/lib/list-items'
import type { SwitchingTimeFinalizeReset } from '~/lib/gantt'
import { useConfirm } from '~/plugins/vue-plugins/use-dialog'
import { Notify } from '~/lib/notify'
import { useI18n } from 'vue-i18n'
import type { EarliestLatestDates, SwitchingsRelatedMap } from '~/lib/switchings'
import { resolveEarliestLatestDates } from '~/lib/switchings'
import { mapPush } from '~/helper'

/**
 * Store just used for debugging purposes
 * Setup-Store Syntax because of watcher
 * - https://pinia.vuejs.org/core-concepts/#Setup-Stores
 * - https://github.com/vuejs/pinia/discussions/794
 */
const switchingsStore = defineStore('switchings', () => {
  const switchings = ref<SwitchingDetailed[]>([])
  // Main
  const switchingsMap = ref(new Map<number, SwitchingDetailed>()) // switching.id -> switching
  const equipmentSwitchingsMap = ref(new Map<number, SwitchingDetailed[]>()) // equipment.id -> switching[]
  const switchingBlockSwitchingsMap = ref(new Map<number, SwitchingDetailed[]>())

  // Other
  const switchingsEquipmentMap = ref(new Map<number, EquipmentRead>()) // switching.id -> equipment
  const switchingsSubProjectMap = ref(new Map<number, SubProjectRead>()) // switching.id -> subProject
  const switchingsProjectMap = ref(new Map<number, ProjectRead>()) // switching.id -> project

  /**
   * Create several relationship maps from SwitchingDetailed
   */
  watch(switchings, () => {
    // Main
    const _switchingsMap = new Map<number, SwitchingDetailed>()
    const _switchingBlockMap = new Map<number, SwitchingDetailed[]>()
    const _equipmentSwitchingsMap = new Map<number, SwitchingDetailed[]>()

    // Other
    const _switchingsEquipmentMap = new Map<number, EquipmentRead>()
    const _switchingsSubProjectMap = new Map<number, SubProjectRead>()
    const _switchingsProjectMap = new Map<number, ProjectRead>()
    for (const switching of switchings.value) {
      // Main
      _switchingsMap.set(switching.id, switching)
      mapPush(_equipmentSwitchingsMap, switching.equipment_id, switching)

      // Other
      _switchingsEquipmentMap.set(switching.id, switching.equipment)
      _switchingsSubProjectMap.set(switching.id, switching.sub_project)
      _switchingsProjectMap.set(switching.id, switching.sub_project.project)
      mapPush(_switchingBlockMap, switching.switching_block_id, switching)
    }

    // Main
    switchingsMap.value = _switchingsMap
    equipmentSwitchingsMap.value = _equipmentSwitchingsMap
    switchingBlockSwitchingsMap.value = _switchingBlockMap

    // Other
    switchingsEquipmentMap.value = _switchingsEquipmentMap
    switchingsSubProjectMap.value = _switchingsSubProjectMap
    switchingsProjectMap.value = _switchingsProjectMap
  })

  return {
    switchings,
    switchingsMap,
    equipmentSwitchingsMap,
    switchingBlockSwitchingsMap,
    switchingsEquipmentMap,
    switchingsSubProjectMap,
    switchingsProjectMap,
  }
})

export type SwitchingsRepository = ReturnType<typeof createSwitchingsRepository>
export function createSwitchingsRepository(
  activityId: number,
  switchingPremises: SwitchingPremisesRepository,
  switchingBlockRestrictions: SBlockRestrictionsRepository,
) {
  const { t } = useI18n()
  const confirm = useConfirm()
  const {
    switchings,
    switchingsMap,
    switchingBlockSwitchingsMap,
    equipmentSwitchingsMap,
    switchingsEquipmentMap,
    switchingsSubProjectMap,
    switchingsProjectMap,
  } = storeToRefs(switchingsStore())
  const columns = useSwitchingColumns(activityId)

  const backend = injectBackendClient()
  const handleFetchError = injectHandleFetchError()

  const switchingCategoryItems: ListItemExtended[] = Object.values(SwitchingCategoryTypeEnum).map<ListItemExtended>(
    (item) => ({
      value: item,
      title: t('SwitchingType.' + item),
      searchString: t('SwitchingType.' + item),
    }),
  )

  /**
   * Hot-update switching data
   */
  function updateSwitchingData(id: number, update: Partial<SwitchingDetailed>) {
    const switching = switchingsMap.value.get(id)
    if (switching) {
      Object.assign(switching, update)
    }
  }

  const fetch = useFetchData.immediate(
    async () => {
      const response = await backend.activity_switchings.switchingsGetDetailedAll(activityId)
      switchings.value = response.data.sort()
    },
    {
      onError: handleFetchError,
    },
  )

  /**
   * Check if switching has a violation
   */
  function switchingHasViolation(switching: SwitchingRead): boolean {
    return (
      switchingBlockRestrictions.restrictionsViolatedSwitchingIds.includes(switching.id) ||
      switchingPremises.premisesViolatedStaticSwitchingIds.value.includes(switching.id) ||
      switchingPremises.premisesViolatedDynamicEquipmentIds.value.includes(switching.equipment_id)
    )
  }

  /**
   * Check if equipment has a violation through its switchings
   */
  function equipmentHasViolations(equipmentId: number): boolean {
    return (equipmentSwitchingsMap.value.get(equipmentId) ?? []).some(switchingHasViolation)
  }

  /**
   * Filter switchings based on the filter event type
   */
  function filterSwitchingsByDateRange<T extends SwitchingDetailed>(
    switchings: T[],
    dateRangeFilter: DateRangeFilter,
  ): T[] {
    if (dateRangeFilter.switchingEvents === SwitchingEventsEnum.IN_RANGE_SWITCHING) {
      return filterItemsByDateRange(switchings, 'end', 'start', dateRangeFilter)
    }
    if (dateRangeFilter.switchingEvents === SwitchingEventsEnum.IN_RANGE_OFFLINE) {
      return filterItemsByDateRange(switchings, 'end_offline', 'start_offline', dateRangeFilter)
    }
    return switchings
  }

  /**
   * Get all equipment projects via its switchings
   */
  function getEquipmentProjects(equipmentId: number): ProjectRead[] {
    const switchings = equipmentSwitchingsMap.value.get(equipmentId)
    if (!switchings) {
      return []
    }
    return switchings.map((switching) => switching.sub_project.project)
  }

  /**
   * Copies either initial_start/end values to start/end or start/end to initial_start/end
   * @param switchings
   * @param finalizeOrReset
   */
  async function finalizeResetSwitchingTime(switchings: SwitchingRead[], finalizeOrReset: SwitchingTimeFinalizeReset) {
    const allSwitchingsToBeUpdated = switchings.map((switching) => {
      if (finalizeOrReset === 'finalize') {
        return {
          id: switching.id,
          start_initial: switching.start,
          end_initial: switching.end,
        }
      } else {
        return {
          id: switching.id,
          start: switching.start_initial,
          end: switching.end_initial,
        }
      }
    })

    return await backend.switchings.switchingsUpdateBulk(allSwitchingsToBeUpdated)
  }

  /**
   * Earliest and Latest dates of the activity
   */
  const earliestLatestDates = ref<EarliestLatestDates>()
  async function earliestLatestDatesInit(): Promise<void> {
    earliestLatestDates.value = await resolveEarliestLatestDates(backend, activityId)
  }

  /**
   * Create a new switching
   */
  async function create(item: SwitchingCreate): Promise<SwitchingDetailed | undefined> {
    try {
      const response = await backend.switchings.switchingsCreate(item)
      const switching = response.data
      switchings.value.unshift(switching)
      Notify.success(t('CreateSuccessful'))
      return switching
    } catch (e) {
      handleFetchError(e)
    }
  }

  /**
   * Update an existing switching
   */
  async function update(item: SwitchingUpdate): Promise<SwitchingDetailed | undefined> {
    try {
      const response = await backend.switchings.switchingsUpdate(item.id, item)
      const switching = response.data
      updateSwitchingData(switching.id, switching)
      Notify.success(t('SaveSuccessful'))
      return switching
    } catch (e) {
      handleFetchError(e)
    }
  }

  /**
   * Delete a switching
   */
  async function deleteConfirm(
    item: number | SwitchingDetailed,
    needsConfirmation = true,
  ): Promise<SwitchingRead | undefined> {
    if (needsConfirmation) {
      const confirmed = await confirm.delete()
      if (!confirmed) {
        return
      }
    }

    try {
      const id = typeof item === 'number' ? item : item.id
      const deleted = await backend.switchings.switchingsDelete(id)
      const index = switchings.value.findIndex((s) => s.id === id)
      if (index >= 0) {
        switchings.value.splice(index, 1)
      }
      Notify.info(t('DeleteSuccessful'))
      return deleted.data
    } catch (e) {
      handleFetchError(e)
    }
  }

  /**
   * Returns all switchings of a given array of `switchingBlockIds`
   */
  function getSwitchingsByBlockIds(switchingBlockIds: number[]): SwitchingsRelatedMap {
    const relatedMap = new Map<number, SwitchingDetailed>()
    for (const switching of switchings.value) {
      if (switchingBlockIds.includes(switching.switching_block_id)) {
        relatedMap.set(switching.id, switching)
      }
    }
    return relatedMap
  }

  const data = {
    switchingCategoryItems: switchingCategoryItems,
    switchings: switchings,
    switchingsMap: switchingsMap,
    equipmentSwitchingsMap: equipmentSwitchingsMap,
    switchingBlockSwitchingsMap: switchingBlockSwitchingsMap,
    switchingsEquipmentMap: switchingsEquipmentMap,
    switchingsSubProjectMap: switchingsSubProjectMap,
    switchingsProjectMap: switchingsProjectMap,

    fetch: fetch,
    create: create,
    update: update,
    deleteConfirm: deleteConfirm,
    updateSwitchingData: updateSwitchingData,
    switchingHasViolation: switchingHasViolation,
    equipmentHasViolations: equipmentHasViolations,
    filterSwitchingsByDateRange: filterSwitchingsByDateRange,
    getEquipmentProjects: getEquipmentProjects,
    finalizeResetSwitchingTime: finalizeResetSwitchingTime,
    getSwitchingsByBlockIds: getSwitchingsByBlockIds,

    earliestLatestDates: earliestLatestDates,
    earliestLatestDatesInit: earliestLatestDatesInit,
    columns,
  }

  provide(SwitchingsRepositorySymbol, data)

  return data
}

export function injectSwitchingsRepository() {
  const repo = inject(SwitchingsRepositorySymbol)
  if (!repo) {
    throw new Error('No Switching Repository provided')
  }
  return repo
}
